import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { FunctionContents, FunctionType, MessageType } from "@/commons/models/Message";
import { SenderType } from "../models/Resident";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 安否確認LINEメッセージ送信 API 操作クラス
 */
export default class SendSafetyConfirmMessages {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET SendMessages
   */
  public async getFamilyMessage(userStore: LoginUser, parentMessagesSortKey: string): Promise<SendFamilySafetyConfirmLineMessagesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: parentMessagesSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendFamilySafetyConfirmMessages, formatParams);

    const response = await this.commonService.executeGet<SendFamilySafetyConfirmLineMessagesGetModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      null,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET SendMessages
   */
  public async getOtherUserMessage(userStore: LoginUser, parentOtherUserMessagesSortKey: string): Promise<SendOtherUserSafetyConfirmLineMessagesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: parentOtherUserMessagesSortKey
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendOtherUserSafetyConfirmMessages, formatParams);

    const response = await this.commonService.executeGet<SendOtherUserSafetyConfirmLineMessagesGetModel>(
      userStore,
      requestUrl,
      this.constructor.name,
      null,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * POST SendFamilyMessages
   */
  public async sendFamilyMessage(userStore: LoginUser, safetyConfirmsId: number, requestBody: FamilySendSafetyConfirmLineMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendFamilySafetyConfirmMessages, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST SendFamilyMails
   */
  public async sendFamilyMails(userStore: LoginUser, safetyConfirmsId: number, requestBody: FamilySendSafetyConfirmMailMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendFamilySafetyConfirmMails, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
  /**
   * POST SendOtherUserMessages
   */
  public async sendOtherUserMessage(userStore: LoginUser, safetyConfirmsId: number, requestBody: OtherUserSendSafetyConfirmLineMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendOtherUserSafetyConfirmMessages, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * POST SendOtherUserMails
   */
  public async sendOtherUserMails(userStore: LoginUser, safetyConfirmsId: number, requestBody: OtherUserSendSafetyConfirmMailMessagesPostModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      safetyConfirmsId: safetyConfirmsId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.SendOtherUserSafetyConfirmMails, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface SendFamilySafetyConfirmLineMessagesGetModel {
  /** ログの際に利用する (YYYYMMDDhhmmssSSS) */
  datetime: number;
  /** 施設を示すID */
  facilityId: number;
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: FunctionContents;
  /** LineのID */
  lineId: string;
  /** メールアドレス */
  mailaddress: string;
  /** 送信種別。LINE:0、MAIL:1 */
  senderType: SenderType;
  /** メッセージの種別を表す */
  messageType: MessageType;
  /** メッセージを受信した側のID */
  receiver: string;
  /** 利用者を示すID */
  residentId: number;
  /** 利用者名 */
  residentName?: string;
  /** 利用者の部屋名 */
  residentRoomName?: string;
  /** 家族を示すID */
  familyId: number;
  /** 家族名 */
  familyName?: string;
  /** 役割 */
  familyPosition?: string;
  /** 関係性 */
  familyRelation?: string;
  /** 家族電話番号 */
  familyTel?: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** メッセージの送信時刻(YYYYMMDDhhmmssSSS) */
  sendTime?: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 編集者 */
  writtenBy?: string;
  /** 編集者のuserId */
  writerId?: string;
  /** opinionのケースID */
  caseId?: string;
  /** ソートキー (residentId)(familyId)(datetime) */
  messagesSortKey: string;
  /** メッセージ受信者詳細リスト */
  receiverDetails?: Array<SendSafetyConfirmReceiverDetail>;
  /** 送信成功LINEユーザーリスト */
  successUsers?: ReceiverDetail[];
  /** 送信失敗LINEユーザーリスト */
  failedUsers?: ReceiverDetail[];
  /** メッセージを既読にした日時(yyyymmddhhMMssSSS)、未読の場合はnullかundefined */
  readDatetime?: number;
}

export interface SendOtherUserSafetyConfirmLineMessagesGetModel {
  /** 施設を示すID */
  facilityId: number;
  /** ソートキー (groupId)(groupMemberId)(datetime) */
  otherUserMessagesSortKey: string;
  /** グループを示すID */
  groupId: number;
  /** グループメンバーを示すID */
  groupMemberId: number;
  /** ログの際に利用する (YYYYMMDDhhmmssSSS) */
  datetime: number;
  /** どの機能が使用されたかを集計するために利用する */
  functionType: FunctionType;
  /** 機能ごとに異なる差分を示すJSON */
  functionContents: SendSafetyConfirmLineMessageFunctionContents;
  /** LineのID */
  lineId: string;
  /** メッセージの種別を表す */
  messageType: MessageType;
  /** メッセージを受信した側のID */
  receiver: string;
  /** メッセージ受信者詳細リスト */
  receiverDetails: Array<SendSafetyConfirmReceiverDetail>;
  /** 施設関係者グループ名 */
  groupName?: string;
  /** 施設関係者グループの種類 ID */
  groupTypeId?: number;
  /** 施設関係者グループメンバー名 */
  groupMemberName?: string;
  /** 施設関係者グループメンバー電話番号 */
  groupMemberTel?: string;
  /** 施設関係者グループメンバー所属 */
  groupMemberOrganization?: string;
  /** メッセージを送信した側のID */
  sender: string;
  /** ケース ID */
  otherUserOpinionMessagesCaseId?: number;
  /** 編集者 */
  writtenBy?: string;
  /** 編集者のuserId */
  writerId?: string;
  /** 送信成功LINEユーザーリスト */
  successUsers?: ReceiverDetail[];
  /** 送信失敗LINEユーザーリスト */
  failedUsers?: ReceiverDetail[];
  /** 既読日時 */
  readDatetime?: number;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface SendSafetyConfirmLineMessageFunctionContents {
  safetyConfirmsId: number;
  title: string;
  openingSentence: string;
  confirmationContents: Array<number>;
}

export interface SendSafetyConfirmReceiverDetail {
  groupName: string;
  lineId: string;
  members: Array<string>;
}

/** メッセージ受信者詳細 */
export interface ReceiverDetail {
  /** 受信者のLINE ID */
  lineId?: string;
  /** 受信者のメールアドレス */
  mailaddress?: string;
  /** 受信者名 */
  name: string;
}

export interface FamilySendSafetyConfirmLineMessagesPostModel {
  /** 宛先情報 */
  targets: Array<SafetyConfirmTarget>;
  /** メッセージ本文 */
  message: string;
  /** 使用する質問ID */
  useIds: Array<number>;
}

export interface SafetyConfirmTarget {
  /** 宛先ソートキー */
  sortKeys: string[];
  /** グループ名 */
  groupName: string;
}

export interface FamilySendSafetyConfirmMailMessagesPostModel {
  /** 宛先情報 */
  targets: Array<SafetyConfirmTarget>;
  /** メッセージ本文 */
  message: string;
}

export interface OtherUserSendSafetyConfirmLineMessagesPostModel {
  /** 宛先情報 */
  targets: Array<SafetyConfirmTarget>;
  /** メッセージ本文 */
  message: string;
  /** 使用する質問ID */
  useIds: Array<number>;
}

export interface OtherUserSendSafetyConfirmMailMessagesPostModel {
  /** 宛先情報 */
  targets: Array<SafetyConfirmTarget>;
  /** メッセージ本文 */
  message: string;
}
